import type {Ref} from "vue"
import type {InputAuthToken, InputVerifyTwoFactorRequest, OutputAuthTokenResponse, OutputUserIdentity} from "~/models";
import {useApi} from "./useApi"
import * as Sentry from "@sentry/browser";
import {getDefaultPageForRole} from "~/utils/roles";

// Пауза між повторами перевірки сесії, коли сервер недосяжний на старті застосунку
const SESSION_RETRY_MS = 5000
let sessionRetryTimer: ReturnType<typeof setTimeout> | undefined

export const useAuth = () => {
    const api = useApi()
    const authState: Ref<OutputUserIdentity | null> = useState("auth")
    const isAuthLoaded: Ref<boolean> = useState('isAuthLoaded', () => false)
    const isAuthenticated: Ref<boolean> = useState('isAuthenticated', () => false)
    const currentRoutePath = useState('currentRoutePath', () => ('/'))

    const getSession = async () => {
        let statusUnauthorized = false
        let serverUnreachable = false
        const userIdentity = await api.getAuthSession()
            .catch(ctx => {
                if (ctx?.response?.status === 401) {
                    statusUnauthorized = true
                    return null
                }
                if (!ctx?.response || isGatewayStatus(ctx.response.status)) {
                    serverUnreachable = true
                }
            })

        // Сесії ще не знаємо, а сервер мовчить (застосунок відкрили без зв'язку). Не робимо
        // висновку «не авторизований» — інакше пілота викине на логін; лишаємось на AppLoader
        // і повторюємо, доки сервер не відповість.
        if (serverUnreachable && !authState.value) {
            clearTimeout(sessionRetryTimer)
            sessionRetryTimer = setTimeout(getSession, SESSION_RETRY_MS)
            return
        }

        if (statusUnauthorized) {
            isAuthenticated.value = false
            authState.value = null
        } else if (userIdentity) {
            if (JSON.stringify(authState.value) != JSON.stringify(userIdentity)) {
                authState.value = userIdentity
                isAuthenticated.value = true
            }

            // Set users identity info
            Sentry.setUser(userIdentity)
        }

        isAuthLoaded.value = true
    }

    const signIn = async (credentials: InputAuthToken): Promise<{requires2FA?: boolean, challenge?: string, hasWebAuthn?: boolean, hasTotp?: boolean, blocked?: boolean, blockedMessageKey?: string, authFailed?: boolean}> => {
        try {
            const response: OutputAuthTokenResponse = await api.getAuthToken({request: credentials})

            // Check if 2FA is required
            if (response.requires2FA) {
                return {
                    requires2FA: true,
                    challenge: response.challenge,
                    hasWebAuthn: response.hasWebAuthn ?? false,
                    hasTotp: response.hasTotp ?? false,
                }
            }

            await nextTick(getSession)
            return {}
        } catch (error: any) {
            // Check if user is blocked (403 on token endpoint = blocked)
            if (error.response?.status === 403) {
                let blockedMessageKey = 'login.blocked'
                try {
                    const bodyText = await error.response.clone().text()
                    const blockedData = JSON.parse(bodyText) as OutputAuthTokenResponse
                    blockedMessageKey = blockedData.blockedMessageKey || 'login.blocked'
                } catch (e) {
                    // If parsing fails, use default key
                }
                return { blocked: true, blockedMessageKey }
            }

            // If error, check if it's a 2FA challenge response
            if (error.response?.data && typeof error.response.data === 'object' && error.response.data.requires2FA) {
                const errorData = error.response.data as OutputAuthTokenResponse
                return {
                    requires2FA: true,
                    challenge: errorData.challenge,
                    hasWebAuthn: errorData.hasWebAuthn ?? false,
                    hasTotp: errorData.hasTotp ?? false,
                }
            }

            // Other auth errors (400, 401, etc.)
            return { authFailed: true }
        }
    }

    const signOut = async () => {
        await api.logoutAuth()
        isAuthenticated.value = false

        Sentry.setUser(null)
    }

    const backToAdmin = async (): Promise<{requires2FA?: boolean, challenge?: string, hasWebAuthn?: boolean, hasTotp?: boolean, blocked?: boolean, authFailed?: boolean}> => {
        try {
            const response: OutputAuthTokenResponse = await useApi().getAuthToken({
                request: {grantType: "admin_comeback"}
            })

            // Check if 2FA is required
            if (response.requires2FA) {
                return {
                    requires2FA: true,
                    challenge: response.challenge,
                    hasWebAuthn: response.hasWebAuthn ?? false,
                    hasTotp: response.hasTotp ?? false,
                }
            }

            Sentry.setUser(null)
            await nextTick(getSession)
            return {}
        } catch (error: any) {
            // Check if user is blocked (403 on token endpoint = blocked)
            if (error.response?.status === 403) {
                return { blocked: true }
            }

            // If error, check if it's a 2FA challenge response
            if (error.response?.data && typeof error.response.data === 'object' && error.response.data.requires2FA) {
                const errorData = error.response.data as OutputAuthTokenResponse
                return {
                    requires2FA: true,
                    challenge: errorData.challenge,
                    hasWebAuthn: errorData.hasWebAuthn ?? false,
                    hasTotp: errorData.hasTotp ?? false,
                }
            }

            // Other auth errors
            console.log('Auth failed:', error.response?.status)
            return { authFailed: true }
        }
    }

    const checkNavigation = (path: string) => {

        const authPage = '/'

        if (!isAuthLoaded.value) {
            return
        }

        if (!isAuthenticated.value && path != '/') {
            navigateTo(authPage)
            return
        }

        if (isAuthenticated.value && path === '/') {
            navigateTo(getDefaultPageForRole(authState.value?.role))
            return
        }

        // if (isAuthenticated.value && authState.value && authState.value.role !== roles.ADMIN.value &&
        //     path.match(/^\/admin\//)) {
        //     navigateTo(mainPage)
        //     return
        // }

        // @todo Напевно краще по стріму передавати що у користувача закінчилась сесія, а не ддосити постійно бек
        if (isAuthenticated.value) {
            getSession()
        }
    }

    callOnce(() => {

        // Перевірка авторизації, одразу під час завантаження застосунку
        getSession()

        watch(currentRoutePath, (path) => {
            checkNavigation(path)
        })

        watch(isAuthLoaded, () => {
            checkNavigation(currentRoutePath.value)
        })
        watch(isAuthenticated, () => {
            checkNavigation(currentRoutePath.value)
        })

        setInterval(() => {
            if (isAuthenticated.value) {
                getSession()
            }
        }, 1000 * 60)
    })

    const verifyTwoFactor = async (challenge: string, method: 'webauthn' | 'totp', options?: {credential?: string, code?: string}) => {
        const api = useApi()
        const request: InputVerifyTwoFactorRequest = {
            challenge,
            method: method === "webauthn" ? "webauthn" : "totp",
            ...(options?.credential && {credential: options.credential}),
            ...(options?.code && {code: options.code}),
        }
        await api.verifyTwoFactor({request})
        await nextTick(getSession)
    }

    return {
        signIn,
        getSession,
        isAuthenticated,
        isAuthLoaded,
        signOut,
        backToAdmin,
        verifyTwoFactor,
        user: authState as Ref<OutputUserIdentity | null>,
        currentRoutePath
    }
}